
export const en = {
  common: {
    close: 'Close',
    copy: 'Copy',
    copied: 'Copied',
    news: 'News',
    more: 'More',
    success: 'Success',
    failed: 'Failed',
    page: 'Page',
    of: 'of',
    loading: 'Loading...',
    error: 'Error',
    search: 'Search',
    reset: 'Reset',
    filter: 'Filter',
    details: 'Details',
    no_results: 'No results found',
    no_description: 'No description available',
    back: 'Back',
    deselect_all: 'Deselect All',
    select_all: 'Select All',
  },
  navbar: {
    news: 'News',
    leaks: 'Leaks',
    cve: 'CVE',
    ransomware: 'Ransomware',
    eol: 'EOL',
    releases: 'Releases',
    dashboard: 'Dashboard',
  },
  index: {
    title: 'CyberMonit - Stay up-to-date with cybersecurity threats',
    description: 'CyberMonit is a platform for monitoring and analyzing cybersecurity threats. We collect data from various sources to provide you with the most up-to-date information about leaks, vulnerabilities, ransomware, and software end-of-life.',
    latest_news: 'Latest News',
    leaks: 'Leaks',
    cve: 'CVE',
    ransomware: 'Ransomware',
    eol: 'EOL',
  },
  news: {
    title: 'Latest News',
    sources: 'News Sources',
    loading: 'Loading news...',
    error: 'Failed to load news',
    readMore: 'Read more',
    page: 'Page',
    of: 'of',
    pageTitle: 'Latest Cybersecurity News',
  },
  leaks: {
    title: 'Latest Data Leaks',
    subtitle: 'Stay informed about the latest data leaks and breaches. Our database is constantly updated with the most recent information.',
    searchPlaceholder: 'Search leaks...',
    noLeaks: 'No leaks found.',
    domain: 'Domain',
    no_domain: 'Unknown Source',
    dataTypes: 'Data Types',
    details: 'View Details',
    breach_stats: 'Breach Statistics',
    records: 'Records',
    data_types: 'Data Types',
    description: 'Description',
    close: 'Close',
    loading: 'Loading leaks...',
    error: 'Failed to load leaks',
    searchResults: '{count} results found',
    noResults: 'No leaks found matching your criteria',
    resetFilters: 'Reset filters',
    pageTitle: 'Data Breach Database',
  },
  cve: {
    title: 'CVE Vulnerabilities',
    subtitle: 'Search for Common Vulnerabilities and Exposures (CVE) information',
    searchPlaceholder: 'Search CVE (e.g., CVE-2023-4863)',
    clearSearch: 'Clear search',
    searching_progress: 'Searching...',
    noSearchResults: 'No CVEs found matching your search criteria',
    searchResults: '{count} CVEs found',
    year_selector: 'Year',
    year_selector_description: 'Select CVE year',
    filter: 'Severity Filter',
    'filter.critical': 'Critical',
    'filter.high': 'High',
    'filter.medium': 'Medium',
    'filter.low': 'Low',
    current_year: 'Current Year',
    details: 'View Details',
    score: 'Score',
    close: 'Close',
    search_all_years: 'Search All Years',
    loading_year: 'Loading {year} data...',
    more_years: 'More Years',
    show_stats: 'Show Statistics',
    hide_stats: 'Hide Statistics',
  },
  ransomware: {
    title: 'Ransomware Attacks',
    description: 'Stay informed about the latest ransomware attacks and protect your data.',
    subtitle: 'Track and monitor the latest ransomware attacks across different countries and sectors',
    countryFilter: 'Country Filter',
    searchPlaceholder: 'Search attacks...',
    showFilter: 'Show Filters',
    details: 'View Details',
    close: 'Close',
    noSearchResults: 'No ransomware attacks found',
    resetFilters: 'Reset Filters',
    filterInstructions: 'Select countries to filter the attacks',
    selectAll: 'Select All',
    deselectAll: 'Deselect All',
    other: 'Other',
    hideFilter: 'Hide Filters',
    searchResults: '{count} results found',
    noFilteredResults: 'No attacks match your current filters',
    showingCountries: 'Showing attacks from {count} countries',
    loading: 'Loading ransomware data...',
    showingResultsFor: 'Showing results for',
    no_attacks_recorded: 'No ransomware attacks recorded',
    top_targets: 'Top Targets',
    attacks: 'Attacks',
    victims: 'Victims',
  },
  eol: {
    title: 'End of Life Software',
    subtitle: 'Track software that has reached its end-of-life and may pose a security risk',
    description: 'Track software that has reached its end-of-life and may pose a security risk.',
    searchPlaceholder: 'Search software...',
    details: 'View Details',
    daysRemaining: 'Days Remaining',
    expired: 'Expired',
    lts: 'LTS',
    no: 'No',
    latestReleaseDate: 'Latest Release Date',
    releaseDate: 'Release Date',
    eolDate: 'EOL Date',
    latestVersion: 'Latest Version',
    pageTitle: 'End of Life Software Tracker',
    all: 'All',
    sortName: 'Sort by Name',
    clearFilters: 'Clear Filters',
    loading: 'Loading EOL data...',
    version: 'Version',
    sortEOL: 'Sort by EOL Date',
    searchResults: '{count} results found',
    statusActive: 'Active',
    statusWarning: 'Warning',
    statusExpired: 'Expired',
  },
  themeToggle: {
    light: 'Light',
    dark: 'Dark',
    system: 'System',
  },
  languageToggle: {
    pl: 'Polish',
    en: 'English',
    change: 'Change language',
  },
  footer: {
    made_by: 'Made by',
    source_code: 'Source code',
    rights: 'All rights reserved',
  },
  releases: {
    title: 'Software Releases',
    description: 'Track the newest software releases and updates from your favorite projects.',
    search_placeholder: 'Search for projects...',
    clear_search: 'Clear search',
    searching: 'Searching...',
    search_results: '{count} results found',
    no_results: 'No projects found matching your search criteria',
    no_data: 'No release data available',
    error_title: 'Could not load releases data',
    error_description: 'There was an error loading the releases data. Please try again later.',
    error_toast: 'Failed to load releases data',
    github_repository: 'Github Repository',
    pageTitle: 'Latest Software Releases',
    release_date: 'Released',
  },
  dashboard: {
    title: 'Dashboard',
    subtitle: 'Key cybersecurity metrics and statistics from the last {days} days',
    pageTitle: 'Cybersecurity Dashboard',
    description: 'View key cybersecurity metrics and statistics from the last 30 days',
    ending_support: 'Ending Support',
    data_leaks: 'Data Leaks',
    active_group: 'Most Active Ransomware Group',
    ransomware_victims: 'Ransomware Victims',
    critical_vulnerabilities: 'Critical Vulnerabilities',
    high_vulnerabilities: 'High Vulnerabilities',
    medium_vulnerabilities: 'Medium Vulnerabilities',
    low_vulnerabilities: 'Low Vulnerabilities',
    last_days: 'Last {days} days',
    most_attacked_country: 'Most Attacked Country',
    latest_news: 'Latest News',
    recent_leaks: 'Recent Data Breaches',
    critical_vulnerabilities_recent: 'Recent Critical Vulnerabilities',
    upcoming_eol: 'Upcoming End of Life',
    latest_releases: 'Latest Releases',
    latest_ransomware: 'Latest Ransomware Attacks',
    ransomware_recent: 'Latest Ransomware Attacks', // Keep this for backward compatibility
    global_ransomware_activity: 'Global Ransomware Activity',
  },
};
